import { useEffect } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { IntegrationType, VoiceIntegration } from '@/shared/types/integration';

import PreferenceCheckbox from './PreferenceCheckbox';

const TELNYX_ENGINES = [
  'Denoiser',
  'DeepFilterNet',
  'Krisp Viva Tel',
  'Krisp Viva Tel Lite',
  'Krisp Viva Pro',
  'Krisp Viva SS',
  'AI-coustics Quail',
] as const;

const MOVEO_ENGINES = ['gtcrn', 'quail-voice-focus'] as const;

type DenoiserProvider = 'moveo' | 'telnyx';
type TelnyxEngine = (typeof TELNYX_ENGINES)[number];
type MoveoEngine = (typeof MOVEO_ENGINES)[number];

const MOVEO_ENGINE_LABEL_KEYS: Record<MoveoEngine, string> = {
  gtcrn: 'integrations.voice_common.engine_gtcrn',
  'quail-voice-focus': 'integrations.voice_common.engine_quail_voice_focus',
};

// English-only on purpose, same rationale as TELNYX_ENGINE_BEST_FOR.
const MOVEO_ENGINE_BEST_FOR: Record<MoveoEngine, string> = {
  gtcrn: 'Lightweight neural noise reduction model',
  'quail-voice-focus': 'Primary speaker isolation against background voices',
};

// Per-engine "best for" caption shown under the engine name in the dropdown.
// Intentionally English-only — paired with the engine value strings (Telnyx
// brand names that are also not translated). Audience is the integrations
// admin, not end users. For deeper specs, link out to the Telnyx engines doc.
const TELNYX_ENGINE_BEST_FOR: Record<TelnyxEngine, string> = {
  Denoiser: 'Default option for most calls',
  DeepFilterNet: 'Telephony and WebRTC',
  'Krisp Viva Tel': 'Telephony with speaker isolation',
  'Krisp Viva Tel Lite': 'Telephony with speaker isolation, lightweight',
  'Krisp Viva Pro': 'Close-microphone WebRTC calls',
  'Krisp Viva SS': 'Smart speakers and far-field microphones',
  'AI-coustics Quail': 'AI and speech recognition workloads',
};

// Direction is always 'inbound' (caller audio) — agent audio doesn't need denoising.
// We set this automatically when provider switches to 'telnyx'; no UI control exposed.
const TELNYX_DEFAULT_DIRECTION = 'inbound' as const;

/**
 * Noise Suppression preference for voice integrations, rendered inside Preferences.
 *
 * Enable + provider (Moveo for all integrations; Telnyx for telnyx-voice only) + engine.
 */
const NoiseSuppression = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<VoiceIntegration>();

  // Read the integration discriminator from form context so this component
  // keeps its zero-prop signature. `useWatch` re-renders on change.
  const integrationType = useWatch<VoiceIntegration>({ name: 'type' }) as
    IntegrationType | undefined;
  const isTelnyx = integrationType === IntegrationType.TELNYX_VOICE;

  const denoiserEnabled = useWatch<VoiceIntegration>({ name: 'config.denoiser.enabled' }) as
    boolean | undefined;
  const denoiserProvider = useWatch<VoiceIntegration>({ name: 'config.denoiser.provider' }) as
    DenoiserProvider | undefined;
  const denoiserMoveoEngine = useWatch<VoiceIntegration>({
    name: 'config.denoiser.moveo.engine',
  }) as MoveoEngine | undefined;

  // Display value coercion: legacy `{enabled: true}` (no provider) reads as Moveo.
  const providerForDisplay: DenoiserProvider = denoiserProvider ?? 'moveo';

  // Legacy-load path only: normalizes pre-existing `{enabled: true}` rows (no provider) to 'moveo'.
  // The enable-via-checkbox case is handled in the Checkbox's onCheckedChange below.
  useEffect(() => {
    if (denoiserEnabled && !denoiserProvider) {
      setValue('config.denoiser.provider', 'moveo', { shouldDirty: false });
    }
  }, [denoiserEnabled, denoiserProvider, setValue]);

  // Default engine for legacy moveo records — yup requires engine when provider='moveo'.
  useEffect(() => {
    if (denoiserEnabled && providerForDisplay === 'moveo' && !denoiserMoveoEngine) {
      setValue('config.denoiser.moveo.engine', 'gtcrn', { shouldDirty: false });
    }
  }, [denoiserEnabled, providerForDisplay, denoiserMoveoEngine, setValue]);

  return (
    <>
      <FormField
        control={control}
        name="config.denoiser.enabled"
        render={({ field }) => (
          <PreferenceCheckbox
            title={t('integrations.voice_common.denoiser_title')}
            description={t('integrations.voice_common.denoiser_description')}
            checked={field.value ?? false}
            onCheckedChange={(checked) => {
              field.onChange(checked);
              if (checked && !denoiserProvider) {
                // Enabling = Moveo + its default engine, so the controls below render with an
                // active provider and a checked engine (not an empty select).
                setValue('config.denoiser.provider', 'moveo', { shouldDirty: true });
              }
              if (checked && !denoiserMoveoEngine) {
                setValue('config.denoiser.moveo.engine', 'gtcrn', { shouldDirty: true });
              }
              if (!checked) {
                // Clear provider + sub-objects so disabled saves as { enabled: false }
                setValue('config.denoiser.provider', undefined, { shouldDirty: true });
                setValue('config.denoiser.moveo', undefined, { shouldDirty: true });
                setValue('config.denoiser.telnyx', undefined, { shouldDirty: true });
              }
            }}
          />
        )}
      />

      {denoiserEnabled ? (
        <FormField
          control={control}
          name="config.denoiser.provider"
          render={() => (
            <FormItem className="pl-7">
              <FormLabel>{t('integrations.voice_common.provider_label')}</FormLabel>
              <FormControl>
                <Select
                  value={providerForDisplay}
                  onValueChange={(value: DenoiserProvider) => {
                    setValue('config.denoiser.provider', value, { shouldDirty: true });
                    if (value === 'telnyx') {
                      setValue('config.denoiser.telnyx.direction', TELNYX_DEFAULT_DIRECTION, {
                        shouldDirty: true,
                      });
                      setValue('config.denoiser.moveo', undefined, { shouldDirty: true });
                    } else {
                      setValue('config.denoiser.telnyx', undefined, { shouldDirty: true });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="moveo">
                      {t('integrations.voice_common.provider_moveo')}
                    </SelectItem>
                    {isTelnyx ? (
                      <SelectItem value="telnyx">
                        {t('integrations.voice_common.provider_telnyx')}
                      </SelectItem>
                    ) : null}
                  </SelectContent>
                </Select>
              </FormControl>
            </FormItem>
          )}
        />
      ) : null}

      {denoiserEnabled && providerForDisplay === 'moveo' ? (
        <FormField
          control={control}
          name="config.denoiser.moveo.engine"
          render={({ field }) => {
            const value = field.value as MoveoEngine | undefined;
            return (
              <FormItem className="pl-7">
                <FormLabel>{t('integrations.voice_common.engine_label')}</FormLabel>
                <FormControl>
                  <Select
                    value={value ?? ''}
                    onValueChange={(next: MoveoEngine) =>
                      setValue('config.denoiser.moveo.engine', next, { shouldDirty: true })
                    }
                  >
                    <SelectTrigger className="w-full">
                      {/* Trigger renders just the engine name, not the rich label below. */}
                      <SelectValue placeholder={t('integrations.voice_common.engine_placeholder')}>
                        {value ? t(MOVEO_ENGINE_LABEL_KEYS[value]) : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {MOVEO_ENGINES.map((engine) => (
                        <SelectItem
                          key={engine}
                          value={engine}
                          textValue={t(MOVEO_ENGINE_LABEL_KEYS[engine])}
                        >
                          <div className="flex flex-col gap-0.5 py-0.5">
                            <span className="font-medium">
                              {t(MOVEO_ENGINE_LABEL_KEYS[engine])}
                            </span>
                            <span className="text-xs text-foreground-muted">
                              {MOVEO_ENGINE_BEST_FOR[engine]}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      ) : null}

      {denoiserEnabled && isTelnyx && providerForDisplay === 'telnyx' ? (
        <FormField
          control={control}
          name="config.denoiser.telnyx.engine"
          render={({ field }) => (
            <FormItem className="pl-7">
              <FormLabel>{t('integrations.voice_common.engine_label')}</FormLabel>
              <FormControl>
                <Select
                  value={(field.value as TelnyxEngine | undefined) ?? ''}
                  onValueChange={(value: TelnyxEngine) =>
                    setValue('config.denoiser.telnyx.engine', value, { shouldDirty: true })
                  }
                >
                  <SelectTrigger className="w-full">
                    {/* Override SelectValue's default rendering so the trigger shows only the
                        engine name — not the rich label + "best for" subtitle from SelectItem. */}
                    <SelectValue placeholder={t('integrations.voice_common.engine_placeholder')}>
                      {field.value as TelnyxEngine | undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {TELNYX_ENGINES.map((engine) => (
                      <SelectItem key={engine} value={engine} textValue={engine}>
                        <div className="flex flex-col gap-0.5 py-0.5">
                          <span className="font-medium">{engine}</span>
                          <span className="text-xs text-foreground-muted">
                            {TELNYX_ENGINE_BEST_FOR[engine]}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </>
  );
};

export default NoiseSuppression;
